import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Ticket as TicketIcon, Plus } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import DynamicCustomFields from "@/components/DynamicCustomFields";

const KINDS = ["support", "noc", "billing", "fraud"];
const PRIORITIES = ["low", "normal", "high", "urgent", "critical"];
const STATUSES = ["new", "open", "in_progress", "waiting_customer", "waiting_internal", "escalated", "resolved", "closed", "reopened"];

const priColor = { critical: "bg-destructive text-destructive-foreground", urgent: "bg-terracotta text-white", high: "bg-warning text-black", normal: "bg-muted text-muted-foreground", low: "bg-muted text-muted-foreground" };

export default function Tickets() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ company_id: "", kind: "support", subject: "", description: "", priority: "normal", severity: "medium", custom_fields: {} });

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    if (filter !== "all") params.kind = filter;
    const [t, c] = await Promise.all([api.get("/tickets", { params }), api.get("/companies")]);
    setItems(t.data); setCompanies(c.data);
    if (!form.company_id) setForm((f) => ({ ...f, company_id: activeCompanyId || c.data[0]?.id || "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", form);
      toast.success("Ticket created");
      setOpen(false); setForm({ ...form, subject: "", description: "", custom_fields: {} }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/tickets/${id}`, { status }); toast.success("Updated"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="tickets-page">
      <PageHeader
        title="Tickets"
        subtitle="Trouble tickets across NOC, Billing, Fraud, and Support — each convertible from an email."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton dataset="tickets" companyId={activeCompanyId} />
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-ticket-button"><Plus className="h-4 w-4 mr-1.5" /> New ticket</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Create ticket</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <F label="Company">
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Kind">
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k.toUpperCase()}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Priority">
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>
                <F label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="ticket-subject-input" /></F>
                <F label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="ticket-description-input" /></F>
                <DynamicCustomFields companyId={form.company_id} value={form.custom_fields} onChange={(v) => setForm({ ...form, custom_fields: v })} />
                <DialogFooter><Button type="submit" className="rounded-sm" data-testid="ticket-create-submit">Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-3">
        <div className="flex items-center gap-2">
          {["all", ...KINDS].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1 text-xs font-mono uppercase tracking-widest border rounded-sm transition-colors ${
                filter === k ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:bg-muted"
              }`}
              data-testid={`ticket-filter-${k}`}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="tbl w-full" data-testid="tickets-table">
            <thead><tr><th>Number</th><th>Subject</th><th>Kind</th><th>Priority</th><th>Status</th></tr></thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} data-testid={`ticket-row-${t.id}`}>
                  <td className="font-mono text-xs">{t.number}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <TicketIcon className="h-3.5 w-3.5 text-terracotta" />
                      <div className="font-medium">{t.subject}</div>
                    </div>
                  </td>
                  <td><Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase">{t.kind}</Badge></td>
                  <td><span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${priColor[t.priority]}`}>{t.priority}</span></td>
                  <td className="w-56">
                    <Select value={t.status} onValueChange={(v) => updateStatus(t.id, v)}>
                      <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`ticket-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="5" className="text-center text-muted-foreground py-8">No tickets yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
